/**
 * EventSourceWatcherStatusStrip — sehr kleiner Statusstreifen auf
 * /mod/event-sources. Zeigt: letzter Lauf, AUTO/ASSISTED/LOCKED-Anzahl,
 * letzter Lauf erfolgreich. Nichts pflegbares — nur Anzeige.
 *
 * Optional "Kör nu"-Knapp för master.
 */

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, RotateCw, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

function ago(iso) {
  if (!iso) return "aldrig";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return "just nu";
  if (min < 60) return `${min} min sedan`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h sedan`;
  const d = Math.round(h / 24);
  return `${d} d sedan`;
}

export default function EventSourceWatcherStatusStrip() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/mod/event-source-watcher/status`, {
        headers: modHeaders(),
      });
      setStatus(res.data);
    } catch (err) {
      // Silent — status är inte kritisk för resten av UI
      // eslint-disable-next-line no-console
      console.warn("watcher status fail", err?.message);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/mod/event-source-watcher/run`,
        {},
        { headers: modHeaders() },
      );
      if (res.data?.skipped) {
        toast.info("Vaktposten körs redan.");
      } else if (res.data?.ok) {
        toast.success("Vaktposten kördes.");
      } else {
        toast.error(`Fel: ${res.data?.last_error || "okänt"}`);
      }
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte köra vaktposten.");
    } finally {
      setBusy(false);
    }
  };

  const last = status?.last || {};
  const bd = status?.breakdown?.breakdown || { AUTO: 0, ASSISTED: 0, LOCKED: 0 };
  const total = status?.breakdown?.total || 0;
  const ok = last.last_run_ok === true;
  const hasRun = !!last.last_run_at;

  return (
    <section
      data-testid="watcher-status-strip"
      className="bg-parchment border border-warm-border rounded-sm px-4 py-2 mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
    >
      <div className="flex items-center gap-1 text-ink">
        <Activity size={14} className="text-pine" />
        <span className="font-medium">Källvaktpost:</span>
      </div>
      <div>
        <span className="text-bark">Totalt: </span>
        <span data-testid="watcher-total" className="text-ink font-medium">{total}</span>
      </div>
      <div>
        <span className="text-bark">AUTO: </span>
        <span data-testid="watcher-auto" className="text-pine font-medium">{bd.AUTO}</span>
      </div>
      <div>
        <span className="text-bark">ASSISTED: </span>
        <span data-testid="watcher-assisted" className="text-amber-900 font-medium">{bd.ASSISTED}</span>
      </div>
      <div>
        <span className="text-bark">LOCKED: </span>
        <span data-testid="watcher-locked" className="text-muted-foreground font-medium">{bd.LOCKED}</span>
      </div>
      <div className="text-muted-foreground">·</div>
      <div>
        <span className="text-bark">Senast körd: </span>
        <span data-testid="watcher-last-run" className="text-ink">{ago(last.last_run_at)}</span>
      </div>
      {hasRun && (
        <div className="inline-flex items-center gap-1">
          {ok ? (
            <>
              <CheckCircle2 size={12} className="text-pine" />
              <span data-testid="watcher-status-ok" className="text-pine">OK</span>
            </>
          ) : (
            <>
              <AlertCircle size={12} className="text-red-700" />
              <span data-testid="watcher-status-error" className="text-red-700" title={last.last_error}>
                Fel
              </span>
            </>
          )}
        </div>
      )}
      <button
        data-testid="watcher-run-now"
        onClick={runNow}
        disabled={busy}
        className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-pine text-pine hover:bg-pine/5 rounded-sm disabled:opacity-50"
      >
        <RotateCw size={12} className={busy ? "animate-spin" : ""} /> Kör nu
      </button>
    </section>
  );
}
